import { forwardRef, useId, useMemo, useState } from 'react';
import { Select, type SelectProps, type SelectOption } from './select';
import { Button } from './button';

export interface SelectableAccount {
  id: string;
  number: string;
  label: string;
  /** A readable group name, shown under the account in the list only. */
  group?: string;
  nature?: string;
  disabled?: boolean;
}
export interface AccountFilters {
  natures?: readonly string[];
  classes?: readonly string[];
  prefixes?: readonly string[];
}
export interface AccountSelectMessages {
  placeholder: string;
  search: string;
  empty: string;
  expand: string;
  unavailable: string;
  loading: string;
  clear: string;
}
/** A line under a document account: an unset value follows `account`. */
export interface AccountInheritance {
  /** Names the inherited choice, e.g. "Compte par défaut". */
  label: string;
  /** What an unset value resolves to; absent while the document has none. */
  account?: SelectableAccount;
}
const defaults: AccountSelectMessages = {
  placeholder: 'Choisir un compte',
  search: 'Rechercher par numéro ou libellé',
  empty: 'Aucun compte compatible',
  expand: 'Voir tous les comptes compatibles',
  unavailable: 'Compte inutilisable',
  loading: 'Chargement des comptes…',
  clear: 'Hériter du compte par défaut',
};
/** The inherited choice's option value: an unset `value` selects it. */
const INHERIT = '';
/**
 * Wide enough for a full account label. Only from `md` up, where the trigger
 * is a narrow column far enough right to grow leftwards; below, the trigger
 * spans the stacked row and a wider list would leave the screen.
 */
const COMPACT_DROPDOWN = 'md:min-w-[24rem]';
export interface AccountSelectProps extends Pick<
  SelectProps,
  | 'id'
  | 'className'
  | 'disabled'
  | 'error'
  | 'size'
  | 'disablePortal'
  | 'aria-label'
  | 'triggerClassName'
  | 'dropdownClassName'
  | 'align'
> {
  /** Business-compatible accounts only. Expansion never changes this boundary. */
  accounts: readonly SelectableAccount[];
  value?: string;
  onChange: (value: string | undefined) => void;
  filters?: AccountFilters;
  recommendedAccountIds?: readonly string[];
  /**
   * Opens on a short list (filters, recommendations, eight accounts) with a
   * link to the rest. Off: every compatible account is listed at once.
   */
  allowExpand?: boolean;
  allowClear?: boolean;
  loading?: boolean;
  required?: boolean;
  name?: string;
  onBlur?: () => void;
  messages?: Partial<AccountSelectMessages>;
  /**
   * For a line under a document account. An unset `value` follows
   * `inherit.account`: the trigger shows it dimmed and the list opens on an
   * option returning to it, in place of the clear link. A line's own account
   * is marked with a dot.
   */
  inherit?: AccountInheritance;
  /**
   * For a narrow table column from `md` up: there, the trigger shows the
   * account number only and the list opens wider, growing to the left.
   * Below `md` it is an ordinary full-label field.
   */
  compact?: boolean;
}
export function normalizeAccountSearch(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
}
export function accountMatchesFilters(
  account: SelectableAccount,
  filters?: AccountFilters,
): boolean {
  return (
    (!filters?.natures?.length ||
      filters.natures.includes(account.nature ?? '')) &&
    (!filters?.classes?.length ||
      filters.classes.includes(account.number[0])) &&
    (!filters?.prefixes?.length ||
      filters.prefixes.some((prefix) => account.number.startsWith(prefix)))
  );
}
export function accountOptions(
  input: Pick<
    AccountSelectProps,
    'accounts' | 'value' | 'filters' | 'recommendedAccountIds'
  > & { expanded: boolean; search: string },
): SelectableAccount[] {
  const recommendations = new Map(
    (input.recommendedAccountIds ?? []).map((id, rank) => [id, rank]),
  );
  const query = normalizeAccountSearch(input.search.trim());
  let accounts = input.accounts.filter((account) =>
    query
      ? normalizeAccountSearch(
          `${account.number} ${account.label} ${account.group ?? ''}`,
        ).includes(query)
      : input.expanded ||
        recommendations.has(account.id) ||
        accountMatchesFilters(account, input.filters),
  );
  accounts = [...accounts].sort(
    (a, b) =>
      (recommendations.get(a.id) ?? Infinity) -
        (recommendations.get(b.id) ?? Infinity) ||
      a.number.localeCompare(b.number, undefined, { numeric: true }),
  );
  if (!input.expanded && !query) accounts = accounts.slice(0, 8);
  const selected = input.accounts.find((account) => account.id === input.value);
  if (selected && !accounts.some((account) => account.id === selected.id))
    accounts.push(selected);
  return accounts;
}
function accountText(account: SelectableAccount): string {
  return `${account.number} — ${account.label}`;
}

export const AccountSelect = forwardRef<HTMLButtonElement, AccountSelectProps>(
  function AccountSelect(
    {
      accounts,
      value,
      onChange,
      filters,
      recommendedAccountIds,
      allowExpand = true,
      allowClear = true,
      loading = false,
      required,
      name,
      onBlur,
      messages,
      inherit,
      compact = false,
      ...props
    },
    ref,
  ) {
    const text = { ...defaults, ...messages };
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState('');
    const statusId = useId();
    const selected = accounts.find((account) => account.id === value);
    const unavailable = Boolean(value && (!selected || selected.disabled));
    const listAll = expanded || !allowExpand;
    const visible = useMemo(
      () =>
        accountOptions({
          accounts,
          value,
          filters,
          recommendedAccountIds,
          expanded: listAll,
          search,
        }),
      [accounts, value, filters, recommendedAccountIds, listAll, search],
    );
    const inherited = inherit?.account;
    const options: SelectOption[] = visible.map((account) => ({
      value: account.id,
      label: accountText(account),
      disabled: account.disabled,
    }));
    if (inherit)
      options.unshift({
        value: INHERIT,
        label: inherited
          ? `${inherit.label} (${accountText(inherited)})`
          : inherit.label,
      });
    if (value && !selected)
      options.push({
        value,
        label: `${text.unavailable} (${value})`,
        disabled: true,
      });
    const accountOf = (option: SelectOption) =>
      accounts.find((candidate) => candidate.id === option.value);
    const renderOption = (option: SelectOption) => {
      const secondary =
        inherit && option.value === INHERIT
          ? inherited
            ? accountText(inherited)
            : '—'
          : accountOf(option)?.group;
      return (
        <span className="min-w-0 text-left">
          <span className="block truncate">
            {inherit && option.value === INHERIT ? inherit.label : option.label}
            {option.disabled ? ` — ${text.unavailable}` : ''}
          </span>
          {secondary && (
            <span className="block truncate text-xs text-muted-foreground">
              {secondary}
            </span>
          )}
        </span>
      );
    };
    // Compact from `md` up: the number is shown, the full text is what a
    // screen reader gets.
    const compactText = (full: string, short: string, own = false) => (
      <>
        <span className="min-w-0 truncate md:hidden">{full}</span>
        <span
          aria-hidden="true"
          className={own ? 'hidden font-medium md:inline' : 'hidden md:inline'}
        >
          {short}
        </span>
        <span className="sr-only max-md:hidden">{full}</span>
      </>
    );
    // One line, whatever the option shows under it: the trigger is a field.
    const renderValue = (option: SelectOption) => {
      if (inherit && option.value === INHERIT) {
        const full = inherited
          ? `${inherit.label} (${inherited.number})`
          : inherit.label;
        return compact ? compactText(full, inherited?.number ?? '—') : full;
      }
      const full = `${option.label}${option.disabled ? ` — ${text.unavailable}` : ''}`;
      const number = accountOf(option)?.number;
      if (!inherit && !compact) return full;
      return (
        <span className="flex min-w-0 items-center gap-1.5">
          {inherit && (
            <span
              aria-hidden="true"
              className="size-1.5 shrink-0 rounded-full bg-riso-blue"
            />
          )}
          {compact && number ? (
            compactText(full, number, true)
          ) : (
            <span className="min-w-0 truncate">{full}</span>
          )}
        </span>
      );
    };
    return (
      <div onBlur={onBlur} aria-busy={loading}>
        {name && <input type="hidden" name={name} value={value ?? ''} />}
        <Select
          align={compact ? 'end' : undefined}
          dropdownClassName={compact ? COMPACT_DROPDOWN : undefined}
          {...props}
          ref={ref}
          value={value ?? INHERIT}
          onChange={(id) => onChange(id || undefined)}
          options={options}
          disabled={props.disabled || loading}
          error={props.error || unavailable}
          searchable
          onSearchChange={setSearch}
          required={required}
          aria-describedby={unavailable ? statusId : undefined}
          placeholder={loading ? text.loading : text.placeholder}
          searchPlaceholder={text.search}
          noResultsText={text.empty}
          filterOption={(option, query) =>
            normalizeAccountSearch(
              `${option.label} ${accountOf(option)?.group ?? ''}`,
            ).includes(normalizeAccountSearch(query))
          }
          renderOption={renderOption}
          renderValue={renderValue}
        />
        {unavailable && (
          <p
            id={statusId}
            role="status"
            className="mt-1 text-sm text-destructive"
          >
            {text.unavailable}
          </p>
        )}
        {((allowExpand && !expanded) ||
          (allowClear && !inherit && value && !required)) && (
          <div className="flex flex-wrap gap-2">
            {allowExpand && !expanded && (
              <Button
                type="button"
                variant="link"
                size="sm"
                disabled={props.disabled || loading}
                onClick={() => setExpanded(true)}
              >
                {text.expand}
              </Button>
            )}
            {allowClear && !inherit && value && !required && (
              <Button
                type="button"
                variant="link"
                size="sm"
                disabled={props.disabled || loading}
                onClick={() => onChange(undefined)}
              >
                {text.clear}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  },
);
